/**
 * Serial Console Module
 *
 * Provides an xterm.js-based terminal for VM serial port access via WebSocket.
 * Unlike the QEMU monitor (JSON request/response), the serial console is a raw
 * bidirectional byte stream — keystrokes are sent directly to the VM's serial
 * port and output is written straight to the terminal.
 */

import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { api } from '../api/client.js';
import { pasteDialog } from '../ui/dialogs.js';

// Module state
let serialFitAddon: FitAddon | null = null;
let serialWebSocket: WebSocket | null = null;

/**
 * Build WebSocket URL for serial console
 */
function buildSerialWsUrl(vmId: string): string {
  return api.buildWsUrl(`/ws/vm/${vmId}/serial`);
}

/**
 * Open the serial console dialog for a VM
 */
export function openSerialConsole(vmId: string, vmName: string): void {
  const dialogId = 'serial-console-dialog';

  // Remove existing dialog and close any existing WebSocket
  $(`#${dialogId}`).remove();
  if (serialWebSocket) {
    serialWebSocket.close();
    serialWebSocket = null;
  }

  const html = `
    <div id="${dialogId}">
      <div class="serial-toolbar">
        <button class="ui-button vnc-btn mobile-kbd-btn" id="serial-keyboard-btn" onclick="serialToggleKeyboard()" title="Toggle On-Screen Keyboard">
          Keyboard
        </button>
        <button class="ui-button vnc-btn" id="serial-paste-btn" onclick="serialPaste()" title="Paste clipboard contents">
          Paste
        </button>
        <span class="serial-status" id="serial-status">Connecting...</span>
      </div>
      <div id="serial-terminal" class="serial-terminal"></div>
    </div>
  `;

  $(html).appendTo('body').dialog({
    modal: true,
    title: `Serial Console - ${vmName}`,
    width: 800,
    height: 500,
    resizable: true,
    close: function () {
      if (serialWebSocket) {
        serialWebSocket.close();
        serialWebSocket = null;
      }
      serialFitAddon = null;
      $(this).remove();
    },
    open: function () {
      initSerialTerminal(vmId);
    },
    resize: function () {
      if (serialFitAddon) {
        serialFitAddon.fit();
      }
    },
  });
}

/**
 * Initialize the xterm.js terminal with a raw WebSocket connection
 */
function initSerialTerminal(vmId: string): void {
  const container = document.getElementById('serial-terminal');
  const statusEl = document.getElementById('serial-status');
  if (!container || !statusEl) return;

  const term = new Terminal({
    cursorBlink: true,
    fontSize: 14,
    fontFamily: 'Consolas, "Courier New", monospace',
    theme: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#ffffff',
      selectionBackground: '#264f78',
      black: '#000000',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      white: '#e5e5e5',
    },
  });

  serialFitAddon = new FitAddon();
  term.loadAddon(serialFitAddon);

  term.open(container);
  serialFitAddon.fit();

  // Connect WebSocket
  const wsUrl = buildSerialWsUrl(vmId);
  serialWebSocket = new WebSocket(wsUrl);

  serialWebSocket.onopen = () => {
    statusEl.textContent = 'Connected';
    statusEl.style.color = '#0a0';
  };

  serialWebSocket.onmessage = (event) => {
    // Raw data from the VM serial port — write directly to terminal
    term.write(event.data as string);
  };

  serialWebSocket.onerror = () => {
    statusEl.textContent = 'Connection error';
    statusEl.style.color = '#c00';
  };

  serialWebSocket.onclose = (event) => {
    statusEl.textContent = event.wasClean ? 'Disconnected' : 'Connection lost';
    statusEl.style.color = event.wasClean ? '#666' : '#c00';
  };

  // Send keystrokes directly to serial port
  term.onData((data) => {
    if (serialWebSocket && serialWebSocket.readyState === WebSocket.OPEN) {
      serialWebSocket.send(data);
    }
  });

  term.focus();

  // Sync mobile keyboard button state when focus is lost externally
  const kbTextarea = container.querySelector('textarea');
  kbTextarea?.addEventListener('blur', updateSerialKeyboardButton);
}

/**
 * Toggle the mobile on-screen keyboard for the serial console.
 * Focuses/blurs xterm.js's internal textarea to show/hide the soft keyboard.
 */
export function toggleSerialKeyboard(): void {
  const terminal = document.getElementById('serial-terminal');
  if (!terminal) return;

  const textarea = terminal.querySelector('textarea');
  if (!textarea) return;

  if (document.activeElement === textarea) {
    textarea.blur();
  } else {
    textarea.focus();
  }

  setTimeout(updateSerialKeyboardButton, 0);
}

function updateSerialKeyboardButton(): void {
  const terminal = document.getElementById('serial-terminal');
  const textarea = terminal?.querySelector('textarea');
  const active = textarea != null && document.activeElement === textarea;
  const btn = document.getElementById('serial-keyboard-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', active);
  }
}

/**
 * Paste clipboard text into the serial console by sending it through the
 * WebSocket in small chunks with delays, so the guest serial buffer
 * doesn't overflow and drop characters.
 */
const PASTE_CHUNK_SIZE = 8;
const PASTE_CHUNK_DELAY_MS = 50;

export function serialPaste(): void {
  if (!serialWebSocket || serialWebSocket.readyState !== WebSocket.OPEN) return;

  pasteDialog((text) => {
    // Normalize line endings to \r for serial (Enter = carriage return)
    const normalized = text.replace(/\r\n/g, '\r').replace(/\n/g, '\r');

    let offset = 0;
    const ws = serialWebSocket;

    function sendChunk(): void {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const chunk = normalized.slice(offset, offset + PASTE_CHUNK_SIZE);
      if (!chunk) return;
      ws.send(chunk);
      offset += chunk.length;
      if (offset < normalized.length) {
        setTimeout(sendChunk, PASTE_CHUNK_DELAY_MS);
      }
    }

    sendChunk();
  });
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).serialToggleKeyboard = toggleSerialKeyboard;
  (window as unknown as Record<string, unknown>).serialPaste = serialPaste;
}
